import React, { useState, useEffect, useRef, useContext } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useReactToPrint } from 'react-to-print';
import AuthContext from '../../context/AuthContext';
import {
  Card,
  Button,
  Row,
  Col,
  Alert,
  Spinner,
  ListGroup,
  Badge,
  Modal,
  Tabs,
  Tab,
  Table
} from 'react-bootstrap';
import { FaUserEdit, FaFileMedical, FaArrowLeft, FaTrash, FaFileInvoiceDollar, FaCalendarPlus, FaCalendarAlt, FaPrint, FaTimes } from 'react-icons/fa';
import './PatientDetail.css';

const PatientDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  
  const [patient, setPatient] = useState(null);
  const [reports, setReports] = useState([]);
  const [bills, setBills] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [queueRecords, setQueueRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [reportToDelete, setReportToDelete] = useState(null);
  const [activeTab, setActiveTab] = useState('reports');
  const [clinic, setClinic] = useState(null);
  
  // Get permissions
  const permissions = user?.role === 'admin' ? {} : (user?.permissions || {});
  const isAdmin = user?.role === 'admin';
  const hasPermission = (key) => isAdmin || permissions[key] === true;
  
  useEffect(() => {
    const fetchPatientData = async () => {
      try {
        setPatient(null);
        setLoading(true);
        
        const [patientRes, reportsRes, billsRes, appointmentsRes, queueRes] = await Promise.all([
          axios.get(`/api/patients.php?id=${id}`),
          axios.get(`/api/reports.php?patientId=${id}`),
          axios.get(`/api/billings.php?patientId=${id}`),
          axios.get(`/api/appointments.php?patientId=${id}`),
          axios.get(`/api/queue.php?patientId=${id}`)
        ]);
        
        setPatient(patientRes.data);
        setReports(Array.isArray(reportsRes.data) ? reportsRes.data : []);
        setBills(Array.isArray(billsRes.data) ? billsRes.data : []);
        setAppointments(Array.isArray(appointmentsRes.data) ? appointmentsRes.data : []);
        setQueueRecords(Array.isArray(queueRes.data) ? queueRes.data : []);
        setLoading(false);
      } catch (err) {
        console.error(err);
        setError('Failed to load patient data');
        setLoading(false);
      }
    };
    
    fetchPatientData();
  }, [id]);
  
  const handleDeleteClick = (report) => {
    setReportToDelete(report);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!reportToDelete) return;
    
    try {
      await axios.delete(`/api/reports.php?id=${reportToDelete.id}`);
      setReports(reports.filter(r => r.id !== reportToDelete.id));
      setShowDeleteModal(false);
      setReportToDelete(null);
    } catch (err) {
      console.error(err);
      setError('Failed to delete report');
    }
  };
  
  const formatCurrency = (amount) => {
    return parseFloat(amount).toLocaleString('en-US', {
      style: 'currency',
      currency: 'PKR'
    });
  };
  
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };
  
  const formatTime = (timeString) => {
    if (!timeString) return 'N/A';
    return timeString;
  };
  
  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge bg="warning">Pending</Badge>;
      case 'partial':
        return <Badge bg="info">Partial</Badge>;
      case 'complete':
        return <Badge bg="success">Complete</Badge>;
      default:
        return <Badge bg="secondary">Unknown</Badge>;
    }
  };
  
  const getAppointmentStatusBadge = (status) => {
    switch (status) {
      case 'scheduled':
        return <Badge bg="primary">Scheduled</Badge>;
      case 'confirmed':
        return <Badge bg="success">Confirmed</Badge>;
      case 'cancelled':
        return <Badge bg="danger">Cancelled</Badge>;
      case 'completed':
        return <Badge bg="info">Completed</Badge>;
      case 'no-show':
        return <Badge bg="warning">No Show</Badge>;
      case 'rescheduled':
        return <Badge bg="warning">Rescheduled</Badge>;
      case null:
      case undefined:
      case '':
        return <Badge bg="primary">Scheduled</Badge>;
      default:
        return <Badge bg="secondary">{status}</Badge>;
    }
  };
  
  const getAppointmentTypeBadge = (type) => {
    switch (type) {
      case 'regular':
        return <Badge bg="primary">Regular</Badge>;
      case 'followup':
        return <Badge bg="info">Follow-up</Badge>;
      case 'emergency':
        return <Badge bg="danger">Emergency</Badge>;
      default:
        return <Badge bg="secondary">Unknown</Badge>;
    }
  };
  
  const handleNewAppointment = () => {
    navigate(`/appointments/add?patientId=${id}`);
  };
  
  if (loading) {
    return (
      <div className="text-center my-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Loading patient data...</p>
      </div>
    );
  }
  
  if (error || !patient) {
    return (
      <Alert variant="danger">
        {error || 'Patient not found'}. <Link to="/patients">Go back to patients list</Link>
      </Alert>
    );
  }
  
  return (
    <div className="patient-detail-container">
      <div className="d-flex justify-content-between align-items-center mb-4 patient-detail-header">
        <h2>Patient Details</h2>
        <div className="patient-detail-actions">
          <Link to="/patients" className="me-2">
            <Button variant="outline-secondary" size="sm">
              <FaArrowLeft className="me-1" /> Back
            </Button>
          </Link>
          {hasPermission('appointments') && (
            <Button variant="info" size="sm" className="me-2" onClick={handleNewAppointment}>
              <FaCalendarPlus className="me-1" /> Schedule Appointment
            </Button>
          )}
          {hasPermission('newReport') && (
            <Link to={`/reports/create/${id}`} className="me-2">
              <Button variant="primary" size="sm">
                <FaFileMedical className="me-1" /> Create Report
              </Button>
            </Link>
          )}
          {hasPermission('createBill') && (
            <Link to={`/billing/create/${id}${appointments.length > 0 && appointments[0].doctorId ? `?doctorId=${appointments[0].doctorId}` : ''}`}>
              <Button variant="success" size="sm">
                <FaFileInvoiceDollar className="me-1" /> Create Bill
              </Button>
            </Link>
          )}
        </div>
      </div>
      
      <Row>
        <Col md={4}>
          <Card className="mb-4">
            <Card.Header>
              <h5 className="mb-0">Patient Information</h5>
            </Card.Header>
            <Card.Body>
              <p>
                <strong>Patient ID:</strong> <span className="patient-id">{patient.patientId}</span>
              </p>
              <p>
                <strong>Name:</strong> {patient.name}
              </p>
              <p>
                <strong>Age:</strong> {patient.age} | <strong>Gender:</strong> {patient.gender}
              </p>
              {patient.bloodGroup && (
                <p>
                  <strong>Blood Group:</strong> {patient.bloodGroup}
                </p>
              )}
              <p>
                <strong>Contact:</strong> {patient.contactNumber}
              </p>
              {patient.address && (
                <p>
                  <strong>Address:</strong> {patient.address}
                </p>
              )}
              
              <div className="mt-4">
                <Link to={`/patients/edit/${id}`}>
                  <Button variant="warning" size="sm">
                    <FaUserEdit className="me-2" /> Edit Patient
                  </Button>
                </Link>
              </div>
            </Card.Body>
          </Card>
          
          {patient.history && (
            <Card className="mb-4">
              <Card.Header>
                <h5 className="mb-0">Medical History</h5>
              </Card.Header>
              <Card.Body>
                <p>{patient.history}</p>
              </Card.Body>
            </Card>
          )}
        </Col>
        
        <Col md={8}>
          <Card>
            <Card.Header>
              <Tabs
                activeKey={activeTab}
                onSelect={(k) => setActiveTab(k)}
                className="mb-0"
              >
                <Tab eventKey="reports" title="Reports">
                </Tab>
                <Tab eventKey="appointments" title="Appointments">
                </Tab>
                <Tab eventKey="bills" title="Bills">
                </Tab>
              </Tabs>
            </Card.Header>
            <Card.Body>
              {activeTab === 'reports' && (
                <>
                  {reports.length > 0 ? (
                    <ListGroup variant="flush">
                      {reports.map(report => (
                        <ListGroup.Item key={report.id}>
                          <div className="d-flex justify-content-between align-items-center">
                            <div>
                              <Link to={`/reports/${report.id}`} className="text-decoration-none">
                                <span className="report-id">{report.reportId || 'N/A'}</span>
                              </Link>
                              <div className="text-muted small">
                                Date: {report.date ? new Date(report.date).toLocaleDateString() : 'N/A'}
                              </div>
                              <div>
                                <strong>Doctor:</strong> {report.doctor?.name || 'N/A'}
                              </div>
                              <div>
                                <Badge bg="info" className="me-1">
                                  {report.diagnosis ? (
                                    report.diagnosis.length > 30
                                      ? report.diagnosis.substring(0, 30) + '...'
                                      : report.diagnosis
                                  ) : 'No diagnosis'}
                                </Badge>
                              </div>
                            </div>
                            <div>
                              <Link to={`/reports/${report.id}`}>
                                <Button 
                                  variant="primary" 
                                  size="sm" 
                                  className="me-2"
                                >
                                  View Detail
                                </Button>
                              </Link>
                              <Button 
                                variant="danger" 
                                size="sm" 
                                onClick={() => handleDeleteClick(report)}
                              >
                                <FaTrash />
                              </Button>
                            </div>
                          </div>
                        </ListGroup.Item>
                      ))}
                    </ListGroup>
                  ) : (
                    <div className="text-center py-4">
                      <p>No reports found for this patient.</p>
                      <Link to={`/reports/create/${id}`}>
                        <Button variant="primary">
                          <FaFileMedical className="me-2" /> Create First Report
                        </Button>
                      </Link>
                    </div>
                  )}
                </>
              )}
              
              {activeTab === 'appointments' && (
                <>
                  {appointments.length > 0 ? (
                    <Table responsive hover>
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Time</th>
                          <th>Doctor</th>
                          <th>Type</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {appointments.map(appointment => (
                          <tr key={appointment.id}>
                            <td>{formatDate(appointment.appointmentDate)}</td>
                            <td>{formatTime(appointment.appointmentTime)}</td>
                            <td>{appointment.Doctor?.name || 'N/A'}</td>
                            <td>{getAppointmentTypeBadge(appointment.appointmentType)}</td>
                            <td>{getAppointmentStatusBadge(appointment.status)}</td>
                            <td>
                              <Link to={`/appointments/edit/${appointment.id}`} className="me-2">
                                <Button variant="primary" size="sm">Edit</Button>
                              </Link>
                              <Link to={`/ticket/appointment/${appointment.id}`}>
                                <Button variant="info" size="sm">Ticket</Button>
                              </Link>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  ) : (
                    <div className="text-center py-4">
                      <p>No appointments found for this patient.</p>
                      <Button variant="info" onClick={handleNewAppointment}>
                        <FaCalendarPlus className="me-2" /> Schedule First Appointment
                      </Button>
                    </div>
                  )}
                </>
              )}
              
              {activeTab === 'bills' && (
                <>
                  {bills.length > 0 ? (
                    <Table responsive hover>
                      <thead>
                        <tr>
                          <th>Bill #</th>
                          <th>Date</th>
                          <th>Amount</th>
                          <th>Paid</th>
                          <th>Payment Status</th>
                          <th>Bill Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {bills.map(bill => (
                          <tr key={bill.id}>
                            <td>{bill.billNumber}</td>
                            <td>{formatDate(bill.createdAt)}</td>
                            <td>{formatCurrency(bill.totalAmount)}</td>
                            <td>{formatCurrency(bill.paidAmount)}</td>
                            <td>{getStatusBadge(bill.paymentStatus)}</td>
                            <td>
                              {bill.billStatus === 'used' ? (
                                <Badge bg="danger">Used</Badge>
                              ) : (
                                <Badge bg="success">Not Used</Badge>
                              )}
                            </td>
                            <td>
                              <Link to={`/billing/${bill.id}`} className="me-2">
                                <Button variant="primary" size="sm">View</Button>
                              </Link>
                              {bill.paymentStatus !== 'complete' && (
                                <Link to={`/billing/${bill.id}/payment`} className="me-2">
                                  <Button variant="success" size="sm">Pay</Button>
                                </Link>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  ) : (
                    <div className="text-center py-4">
                      <p>No bills found for this patient.</p>
                      <Link to={`/billing/create/${id}${appointments.length > 0 && appointments[0].doctorId ? `?doctorId=${appointments[0].doctorId}` : ''}`}>
                        <Button variant="success">
                          <FaFileInvoiceDollar className="me-2" /> Create First Bill
                        </Button>
                      </Link>
                    </div>
                  )}
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete this report?
          {reportToDelete?.date && (
            <p>Date: <strong>{new Date(reportToDelete.date).toLocaleDateString()}</strong></p>
          )}
          <p className="mt-2 text-danger">
            <strong>Warning:</strong> This action cannot be undone.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleConfirmDelete}>
            Delete Report
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default PatientDetail;
